/**
 * The package plumbing every Office format shares: relationships, part paths,
 * content types, and repacking the parts a preview kept into a file the
 * existing viewers open as they would the original.
 *
 * Plain string work rather than DOMParser: these are machine-written parts
 * read for a handful of attributes, and a preview is on the clock.
 */

import JSZip from 'jszip'

import { getOpcRelationshipPartName } from '@/lib/ooxml/package'
import { type OpenZip, readEntries } from '@/lib/ooxml/rangeArchive'

export interface Relationship {
  id: string
  type: string
  target: string
  external: boolean
  /** The element as written, for putting back unchanged. */
  xml: string
}

const RELATIONSHIP = /<Relationship\b[^>]*?\/>/g
const attribute = (xml: string, name: string) =>
  new RegExp(`\\b${name}="([^"]*)"`).exec(xml)?.[1] ?? ''

export const parseRels = (xml: string): Relationship[] =>
  (xml.match(RELATIONSHIP) ?? []).map((element) => ({
    id: attribute(element, 'Id'),
    type: attribute(element, 'Type').split('/').pop() ?? '',
    target: attribute(element, 'Target'),
    external: attribute(element, 'TargetMode') === 'External',
    xml: element,
  }))

export const writeRels = (rels: Relationship[]) =>
  '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
  '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
  rels.map((rel) => rel.xml).join('') +
  '</Relationships>'

const dirname = (path: string) => path.slice(0, path.lastIndexOf('/') + 1)

/**
 * A relationship target, resolved against the part that names it, as the name
 * the zip holds the part under. Not resolveOpcRelationshipTarget: that decodes
 * the target to match the package reader, which decodes entry names too. Here
 * entries keep the names they were written with, and a conformant package
 * percent-encodes those just as it does its targets (`media/figure%201.png`);
 * the zip index finds a part written with a literal name as well.
 */
export const resolveTarget = (partPath: string, target: string) => {
  // A fragment addresses something inside the part, not another part.
  const path = target.split('#', 1)[0] ?? ''
  const joined = path.startsWith('/')
    ? path.slice(1)
    : `${dirname(partPath)}${path}`
  const out: string[] = []
  for (const segment of joined.split('/')) {
    if (segment === '..') out.pop()
    else if (segment !== '.' && segment !== '') out.push(segment)
  }
  return out.join('/')
}

/** Relationship ids a part's XML points at (r:embed, r:id, r:link, …). */
export const referencedRelIds = (xml: string) =>
  new Set(
    [
      ...xml.matchAll(/\br:(?:embed|id|link|pict|dm|lo|qs|cs|href)="([^"]+)"/g),
    ].map((match) => match[1]),
  )

/** [Content_Types].xml with overrides only for the parts that were kept. */
export const rewriteContentTypes = (xml: string, kept: Set<string>) =>
  xml.replace(/<Override\b[^>]*?\/>/g, (element) =>
    kept.has(attribute(element, 'PartName').replace(/^\//, '')) ? element : '',
  )

export const decodeParts = (parts: Map<string, Uint8Array>) => {
  const decoder = new TextDecoder()
  const text = new Map<string, string>()
  for (const [name, bytes] of parts) text.set(name, decoder.decode(bytes))
  return text
}

const partReads = new WeakMap<OpenZip, Map<string, Promise<Uint8Array>>>()

/**
 * Reads whichever of these parts exist; missing ones are simply absent. A part
 * already asked for — by an earlier read or a prefetch — is not fetched again.
 */
export const readParts = async (
  zip: OpenZip,
  paths: Iterable<string>,
): Promise<Map<string, Uint8Array>> => {
  let reads = partReads.get(zip)
  if (!reads) {
    reads = new Map()
    partReads.set(zip, reads)
  }
  const wanted = [...new Set(paths)].filter((path) => zip.entries.has(path))
  const missing = wanted.filter((path) => !reads.has(path))
  if (missing.length > 0) {
    const batch = readEntries(
      zip.source,
      missing.map((path) => zip.entries.get(path)!),
    )
    for (const path of missing) {
      // Read under the entry's own name, which may be spelled otherwise.
      const name = zip.entries.get(path)!.name
      const read = batch.then((parts) => parts.get(name)!)
      // Handled here so a prefetched part nobody ends up reading never
      // surfaces as an unhandled rejection; readers still see the error.
      read.catch(() => undefined)
      reads.set(path, read)
    }
  }
  const parts = await Promise.all(
    wanted.map(async (path) => [path, await reads.get(path)!] as const),
  )
  return new Map(parts)
}

/**
 * Starts reading parts a package almost always has under their conventional
 * names, in one round trip, before the relationships that name them have
 * been read. A guess that misses costs a few kilobytes; one that hits saves
 * a round trip per step down the chain.
 */
export const prefetchParts = (
  zip: OpenZip,
  paths: string[],
  maxBytes: number,
) => {
  void readParts(
    zip,
    paths.filter(
      (path) =>
        (zip.entries.get(path)?.uncompressedSize ?? Infinity) <= maxBytes,
    ),
  ).catch(() => undefined)
}

/** Parts a guess reads ahead of time are small XML; anything larger waits to be asked for. */
export const PREFETCH_MAX_BYTES = 512 * 1024

/**
 * The kept parts as a new package. Stored, not deflated: it lives for one
 * parse, and compressing it again would only spend the time saved.
 */
export const packStore = async (
  files: Map<string, string | Uint8Array>,
  type: string,
  name: string,
): Promise<File> => {
  const zip = new JSZip()
  for (const [path, content] of files) zip.file(path, content)
  const blob = await zip.generateAsync({ type: 'blob', compression: 'STORE' })
  return new File([blob], name, { type })
}

export const officeDocumentPath = (rootRelsXml: string) => {
  const rel = parseRels(rootRelsXml).find((r) => r.type === 'officeDocument')
  if (!rel) throw new Error('Package has no main document')
  return resolveTarget('', rel.target)
}

/** Keeps only the root relationship to the main document. */
export const rootRelsFor = (rootRelsXml: string) =>
  writeRels(parseRels(rootRelsXml).filter((r) => r.type === 'officeDocument'))

/**
 * Relationships a preview never follows: embedded workbooks behind charts,
 * OLE payloads, speaker notes, comments. Dropping them from the rels keeps
 * the package self-consistent.
 */
const SKIPPED_TYPES = new Set([
  'package',
  'oleObject',
  'notesSlide',
  'comments',
  'commentAuthors',
  'printerSettings',
  'customXml',
])

/** Parts and, transitively, whatever their own relationships name. */
export const collectDependencies = async (
  zip: OpenZip,
  paths: string[],
  into: Map<string, string | Uint8Array>,
) => {
  let pending = paths.filter((path) => !into.has(path) && zip.entries.has(path))
  while (pending.length > 0) {
    const relsPaths = pending
      .map(getOpcRelationshipPartName)
      .filter((path) => zip.entries.has(path))
    const [relsParts, parts] = await Promise.all([
      readParts(zip, relsPaths),
      readParts(zip, pending),
    ])
    const read = decodeParts(relsParts)
    const next: string[] = []
    for (const [path, bytes] of parts) into.set(path, bytes)
    for (const [relsPath, xml] of read) {
      const owner = pending.find(
        (path) => getOpcRelationshipPartName(path) === relsPath,
      )!
      const kept = parseRels(xml).filter(
        (rel) =>
          !SKIPPED_TYPES.has(rel.type) &&
          (rel.external || zip.entries.has(resolveTarget(owner, rel.target))),
      )
      into.set(relsPath, writeRels(kept))
      for (const rel of kept) {
        if (rel.external) continue
        const target = resolveTarget(owner, rel.target)
        if (!into.has(target)) next.push(target)
      }
    }
    pending = [...new Set(next)]
  }
}
